import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonService } from '../../../services/common/common.service';
import { Endpoint, HelpIndex } from '../../../models/enums';
import { AlignDirection, CellTemplateType, FilterTemplateType, GenericColDef, GridContext } from '../../../models/grid-context';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { RestService } from '../../../services/rest/rest.service';
import { GenericGridComponent } from '../../generic-grid/generic-grid.component';
import { ProjectAccessHistoryNode } from '../../../models/project-access-history-node';

@Component({
  standalone: false,
  selector: 'res-project-access-history',
  templateUrl: './project-access-history.component.html',
  styleUrls: ['./project-access-history.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ProjectAccessHistoryComponent implements OnInit {
  @ViewChild(GenericGridComponent, { static: true }) projectAccessHistoryGrid: GenericGridComponent;
  @ViewChild('downloadLink', { read: ElementRef, static: true }) downloadLink: ElementRef;

  automationId = 'project-access-history';
  gridName = 'Project Access History';
  isBusy = false;
  isBusyMessage = 'Loading Project Access History ...';
  downloadTooltip = 'Download filtered (.xlsx)';

  requestInfo = new DrRequestInfo({
    endpoint: Endpoint.realmManager_listProjectAccessHistory,
    responseName: 'logs',
    data: { systemScope: true }
  });

  gridContext = new GridContext({
    sortBy: 'accessTime',
    sortAscendingFieldName: 'descending',
    sortAscending: false
  });

  columnDefs: Array<GenericColDef> = new Array<GenericColDef>(
    {
      headerName: 'Username',
      field: 'userName',
      automationId: this.automationId,
      minWidth: 30,
      lockVisible: true,
      filterType: FilterTemplateType.freeform,
      suppressMovable: true,
      serviceTierFilter: true,
      filterAttributeName: 'USER',
    },
    {
      headerName: 'Role',
      field: 'role',
      automationId: this.automationId,
      minWidth: 30,
      suppressMovable: true,
      filterType: FilterTemplateType.singleChoice,
      serviceTierFilter: true,
      filterAttributeName: 'ROLETYPE',
    },
    {
      headerName: 'First Name',
      field: 'firstName',
      automationId: this.automationId,
      minWidth: 30,
      filterType: FilterTemplateType.freeform,
      suppressMovable: true,
      serviceTierFilter: true,
      filterAttributeName: 'FIRST_NAME',
    },
    {
      headerName: 'Last Name',
      field: 'lastName',
      automationId: this.automationId,
      minWidth: 30,
      filterType: FilterTemplateType.freeform,
      suppressMovable: true,
      serviceTierFilter: true,
      filterAttributeName: 'LAST_NAME',
    },
    {
      headerName: 'Email',
      field: 'email',
      automationId: this.automationId,
      minWidth: 30,
      filterType: FilterTemplateType.freeform,
      suppressMovable: true,
      serviceTierFilter: true,
      filterAttributeName: 'EMAIL',
    },
    {
      headerName: 'Project Name',
      field: 'projectName',
      automationId: this.automationId,
      minWidth: 30,
      filterType: FilterTemplateType.freeform,
      suppressMovable: true,
      serviceTierFilter: true,
      filterAttributeName: 'PROJECT',
    },
    {
      headerName: 'Project Description',
      field: 'projectDescription',
      automationId: this.automationId,
      minWidth: 30,
      filterType: FilterTemplateType.freeform,
      suppressMovable: true,
      serviceTierFilter: true,
      filterAttributeName: 'DESCRIPTION',
    },
    {
      headerName: 'Organization',
      field: 'organizationName',
      automationId: this.automationId,
      minWidth: 30,
      filterType: FilterTemplateType.comboBox,
      suppressMovable: true,
      serviceTierFilter: true,
      filterAttributeName: 'ORG',
    },
    {
      headerName: 'Access Date',
      field: 'accessTime',
      automationId: this.automationId,
      initialWidth: 145, minWidth: 145, maxWidth: 145,
      sortBy: 'accessTime',
      headerAlignDirection: AlignDirection.right,
      cellAlignDirection: AlignDirection.right,
      cellTemplateType: CellTemplateType.date,
      filterType: FilterTemplateType.date,
      filterAttributeName: 'ACCESS_TIME',
      suppressMovable: true,
      serviceTierFilter: true,
    },
    {
      headerName: 'IP Address',
      field: 'ipAddress',
      automationId: this.automationId,
      initialWidth: 120, minWidth: 30,
      suppressMovable: true
    },
    {
      headerName: 'Duration',
      field: 'duration',
      automationId: this.automationId,
      minWidth: 30,
      initialWidth: 125,
    },
  );

  constructor(private commonService: CommonService, private restService: RestService) { }

  responseArrayCallbackProjectAccessHistoryNodes = (projectAccessHistoryNode: ProjectAccessHistoryNode) => new ProjectAccessHistoryNode(projectAccessHistoryNode);

  ngOnInit(): void {
    const promiseList = [];

    promiseList.push(this.restService.post(Endpoint.realmManager_listProjectAccessRole, { systemScope: true }).then(response => {
      const roleColDef = this.columnDefs.find(f => f.field === 'role');
      if (response && roleColDef) {
        roleColDef.filterItems = ['All', ...response?.roles];
        roleColDef.filterItems.sort();
        roleColDef.filterItemSelected = 'All';
      }
    }));

    promiseList.push(this.restService.post(Endpoint.realmManager_listOrganizations, { systemScope: true }).then(response => {
      const orgNameColDef = this.columnDefs.find(f => f.field === 'organizationName');
      if (response && orgNameColDef) {
        orgNameColDef.filterItems = response?.organizations.map(o => o.name).sort();
      }
    }));

    Promise.all(promiseList).then(() => {
      if (this.projectAccessHistoryGrid) {
        this.projectAccessHistoryGrid.setColumnDefs(this.columnDefs);
        this.projectAccessHistoryGrid.refreshColumns();
      }
    });
  }

  downloadProjectAccessHistoryReport(): void {
    const endpoint = Endpoint.realmManager_getDownloadProjectAccessHistoryUrl;
    const postParams = {
      filters: this.requestInfo?.data?.filters
    };
    this.restService.post(endpoint, postParams).then(response => {
      if (response) {
        this.commonService.unzipFile(response?.url, this.downloadLink.nativeElement, 'ProjectAccessHistoryReport');
      }
    })
  }

  openHelp = (): void => this.commonService.openHelp(HelpIndex.sys_proj_access_history_dlg);
}
